/**
 * FilePreview Component
 *
 * Preview files based on their type (images, PDFs, videos, audio).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, ZoomIn, ZoomOut, RotateCw, Loader2, File, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { api, UserFileResponse } from '@/lib/api';

export interface FilePreviewProps {
  /** File to preview */
  file: UserFileResponse;
  /** Callback when preview is closed */
  onClose?: () => void;
  /** Additional CSS classes */
  className?: string;
}

type PreviewType = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'unsupported';

/**
 * File preview component
 */
export function FilePreview({ file, onClose, className }: FilePreviewProps) {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [textContent, setTextContent] = useState<string | null>(null);

  const previewType = getPreviewType(file.mimeType ?? undefined);

  // Fetch download URL
  useEffect(() => {
    async function fetchUrl() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.getDownloadUrl(file.fileId);
        setDownloadUrl(response.downloadUrl);

        // For text files, fetch content
        if (previewType === 'text') {
          const textResponse = await fetch(response.downloadUrl);
          const text = await textResponse.text();
          setTextContent(text.slice(0, 100000)); // Limit to 100KB
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load preview');
      } finally {
        setIsLoading(false);
      }
    }

    fetchUrl();
  }, [file.fileId, previewType]);

  const handleDownload = useCallback(() => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  }, [downloadUrl]);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.25, 0.25));
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      }
    },
    [onClose, handleZoomIn, handleZoomOut],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Lock body scroll while preview is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className={cn('fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur', className)}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-medium truncate max-w-md">{file.fileName}</h2>
          <span className="text-sm text-muted-foreground">{formatBytes(file.sizeBytes)}</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Image controls */}
          {previewType === 'image' && (
            <>
              <Button variant="ghost" size="icon" onClick={handleZoomOut}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button variant="ghost" size="icon" onClick={handleZoomIn}>
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleRotate}>
                <RotateCw className="h-4 w-4" />
              </Button>
              <div className="w-px h-6 bg-border mx-2" />
            </>
          )}

          <Button variant="ghost" size="icon" onClick={handleDownload}>
            <Download className="h-4 w-4" />
          </Button>

          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex items-center justify-center p-4">
        {isLoading ? (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        ) : error ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <p className="text-lg font-medium">Failed to load preview</p>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download instead
            </Button>
          </div>
        ) : (
          <PreviewContent
            type={previewType}
            url={downloadUrl!}
            textContent={textContent}
            zoom={zoom}
            rotation={rotation}
            fileName={file.fileName}
          />
        )}
      </div>
    </div>
  );
}

function PannableImage({
  url,
  fileName,
  zoom,
  rotation,
}: {
  url: string;
  fileName: string;
  zoom: number;
  rotation: number;
}) {
  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const panOffset = useRef({ x: 0, y: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Reset pan offset when zoom returns to 1
  useEffect(() => {
    if (zoom <= 1) {
      panOffset.current = { x: 0, y: 0 };
      setOffset({ x: 0, y: 0 });
    }
  }, [zoom]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0 || zoom <= 1) return;
      isDragging.current = true;
      lastPos.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    },
    [zoom],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      panOffset.current = {
        x: panOffset.current.x + dx,
        y: panOffset.current.y + dy,
      };
      setOffset({ ...panOffset.current });
      lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div
      className="overflow-hidden w-full h-full flex items-center justify-center"
      style={{ cursor: zoom > 1 ? (isDragging.current ? 'grabbing' : 'grab') : 'default' }}
      onMouseDown={handleMouseDown}
    >
      <img
        src={url}
        alt={fileName}
        draggable={false}
        className="max-w-full max-h-full object-contain"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
          transformOrigin: 'center center',
        }}
      />
    </div>
  );
}

interface PreviewContentProps {
  type: PreviewType;
  url: string;
  textContent: string | null;
  zoom: number;
  rotation: number;
  fileName: string;
}

function PreviewContent({ type, url, textContent, zoom, rotation, fileName }: PreviewContentProps) {
  switch (type) {
    case 'image':
      return <PannableImage url={url} fileName={fileName} zoom={zoom} rotation={rotation} />;

    case 'video':
      return (
        <video src={url} controls className="max-w-full max-h-full" autoPlay={false}>
          Your browser does not support the video tag.
        </video>
      );

    case 'audio':
      return (
        <div className="flex flex-col items-center gap-4">
          <File className="h-24 w-24 text-muted-foreground" />
          <p className="text-lg font-medium">{fileName}</p>
          <audio src={url} controls className="w-full max-w-md">
            Your browser does not support the audio tag.
          </audio>
        </div>
      );

    case 'pdf':
      return <iframe src={url} title={fileName} className="w-full h-full border-0" />;

    case 'text':
      return (
        <div className="w-full max-w-4xl">
          <pre className="p-4 rounded-lg bg-muted overflow-auto text-sm font-mono whitespace-pre-wrap">
            {textContent}
          </pre>
        </div>
      );

    case 'unsupported':
    default:
      return (
        <div className="flex flex-col items-center gap-4 text-center">
          <File className="h-24 w-24 text-muted-foreground" />
          <p className="text-lg font-medium">{fileName}</p>
          <p className="text-sm text-muted-foreground">Preview not available for this file type</p>
          <Button asChild>
            <a href={url} target="_blank" rel="noopener noreferrer">
              <Download className="h-4 w-4 mr-2" />
              Download
            </a>
          </Button>
        </div>
      );
  }
}

function getPreviewType(mimeType?: string): PreviewType {
  if (!mimeType) return 'unsupported';

  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  if (
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml' ||
    mimeType === 'application/javascript'
  ) {
    return 'text';
  }

  return 'unsupported';
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default FilePreview;
